/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import { useNavigate } from 'react-router-dom';

import HoverPopover from 'material-ui-popup-state/HoverPopover';
import PopupState, { bindPopover, bindHover } from 'material-ui-popup-state';

import MenuDivider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import UserIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import BilligIcon from '@mui/icons-material/Payments';
import MenuItem from '@mui/material/MenuItem';
import Text from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import MoreMenuItem from '../common/MoreMenuItem';
import SpinnerModal from '../common/SpinnerModal';
import { logout, useUserActions } from '../../actions/UserActions';
import { fetchCustomerPortalUrl } from '../../actions/WorkspaceActions';
import { track } from '../../utils/track';
import { useClientLanguages } from '../../utils/hooks';

import { addSnack } from '../../store';

let timeout;

function wrappedBindHover(popupState) {
  const { onMouseLeave, ...rest } = bindHover(popupState);

  function _onMouseLeave(e) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      onMouseLeave(e);
    }, 150);
  }

  return {
    ...rest,
    onMouseLeave: _onMouseLeave,
  };
}

export default function Profile({ user }) {
  const [customerPortalLoading, setCustomerPortalLoading] = useState(false);
  const [language, setLanguage] = useState(user?.language);
  const [workspaceId, setWorkspaceId] = useState(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { updateUserAsync, switchWorkspaceAsync } = useUserActions();

  const workspace = user?.workspace;

  const clientLanguages = useClientLanguages();

  const workspacesOptions = useMemo(() => {
    if (user?.organizations) {
      const orgs = user?.organizations?.map((org) => ({
        label: org.name,
        value: org.id,
      }));
      return orgs;
    }
    return [];
  }, [user]);

  useEffect(() => {
    setWorkspaceId(user?.workspace?.id);
    setLanguage(user?.language);
  }, [user]);

  async function handleWorkspaceChange(workspaceId) {
    track('workspace_change');
    await switchWorkspaceAsync(workspaceId);
    setWorkspaceId(workspaceId);
    navigate('/');
  }

  function handleNavigate(item) {
    if (item === 'log_out') {
      logout();

      // Clear session storage in event microsoft login was used
      //window.sessionStorage.clear();
      window.location = '/login';
      return;
    }

    if (item) {
      navigate(`/${item}`);
    }
  }

  function onLogout() {
    logout();
    window.location = '/login';
  }

  const gotoCustomerPortal = useCallback(async () => {
    try {
      track('customer_portal_clicked');
      if (!workspace) return;
      setCustomerPortalLoading(true);
      const { url } = await fetchCustomerPortalUrl({ id: workspace.id });
      window.location.href = url;
    } catch (e) {
      addSnack({
        msg: t('Något gick fel när vi skulle öppna sidan'),
        variant: 'error',
      });
    } finally {
      setCustomerPortalLoading(false);
    }
  }, [workspace, t]);

  async function handleLanguageChange(lang) {
    track('language_change', { lang });
    setLanguage(lang);
    i18n.changeLanguage(lang);
    await updateUserAsync({ language: lang });
  }

  // PLace menu to the right of the button

  return (
    <PopupState variant="popover" popupId="aside-profile-popover">
      {(popupState) => (
        <>
          <div {...wrappedBindHover(popupState)}>
            <IconButton
              css={css`
                border-radius: 50%;
                height: 64px;
                width: 64px;
                background: rgba(255, 255, 255, 0);
              `}
              disableRipple
            >
              <Avatar
                css={css`
                  background: var(--primary-100);
                  color: rgb(101, 104, 117);
                `}
              />
            </IconButton>
          </div>
          <HoverPopover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onMouseEnter={() => {
              if (timeout) {
                clearTimeout(timeout);
              }
            }}
          >
            <ListItemText
              css={{
                padding: '0.5rem 1rem',
              }}
            >
              <Text css={{ fontWeight: 'bold' }}>{user?.email}</Text>
            </ListItemText>
            <MenuDivider />
            {workspacesOptions.length > 1 ? (
              <MoreMenuItem
                key="workspace"
                css={css`
                  margin: 8px 0;
                `}
                label={
                  <>
                    <span>
                      <b>{t('Arbetsyta')}</b>:{' '}
                      {
                        workspacesOptions.find((w) => w.value === workspaceId)
                          ?.label
                      }
                    </span>
                    <ListItemIcon css={{ marginLeft: 'auto' }}>
                      <ChevronRightIcon />
                    </ListItemIcon>
                  </>
                }
              >
                {workspacesOptions.map((w) => (
                  <MenuItem
                    key={w.value}
                    onClick={() => handleWorkspaceChange(w.value)}
                  >
                    {w.label}
                  </MenuItem>
                ))}
              </MoreMenuItem>
            ) : null}
            {workspacesOptions.length === 1 ? (
              <ListItemText
                css={{
                  fontWeight: 'bold',
                  color: 'var(--text-primary) !important',
                  padding: '0.5rem 1rem',
                }}
              >
                <span>
                  <b>{t('Arbetsyta')}</b>: {workspacesOptions[0].label}
                </span>
              </ListItemText>
            ) : null}
            <MenuDivider />
            <MoreMenuItem
              key="language"
              css={css`
                margin: 8px 0;
              `}
              label={
                <>
                  <span>
                    <b>{t('Språk')}</b>:{' '}
                    {clientLanguages.find((l) => l.value === language)?.label}
                  </span>
                  <ListItemIcon css={{ marginLeft: 'auto' }}>
                    <ChevronRightIcon />
                  </ListItemIcon>
                </>
              }
            >
              {clientLanguages.map((lang) => (
                <MenuItem
                  key={lang.label}
                  onClick={() => handleLanguageChange(lang.value)}
                >
                  {lang.label}
                </MenuItem>
              ))}
            </MoreMenuItem>
            <MenuDivider />
            <MenuItem onClick={() => handleNavigate('profile')}>
              <UserIcon
                css={css`
                  margin-right: 0.5rem;
                `}
              />
              {t('Profil')}
            </MenuItem>
            {user?.workspace_role === 'admin' ? (
              <MenuItem onClick={() => handleNavigate('workspace/admin')}>
                <SettingsIcon
                  css={css`
                    margin-right: 0.5rem;
                  `}
                />
                {t('Arbetsytans inställningar')}
              </MenuItem>
            ) : null}
            {workspace?.type === 'stripe' &&
            user?.workspace_role === 'admin' ? (
              <MenuItem onClick={gotoCustomerPortal}>
                <BilligIcon
                  css={css`
                    margin-right: 0.5rem;
                  `}
                />
                {t('Hantera prenumeration')}
              </MenuItem>
            ) : null}
            {user?.role === 'admin' ? (
              <>
                <MenuDivider />
                <MenuItem onClick={() => handleNavigate('customers')}>
                  {t('Kunder')}
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('workspaces')}>
                  {t('Arbetsytor')}
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('users')}>
                  {t('Användare')}
                </MenuItem>
              </>
            ) : null}
            <MenuDivider />
            {(user?.role === 'single-tenant-admin' || user?.role === 'admin') && (
              <>
                <Text>{t('Din instans')}</Text>
                <MenuItem onClick={() => handleNavigate('single-tenant/workspaces')}>
                  {t('Arbetsytor')}
                </MenuItem>
                <MenuItem onClick={() => handleNavigate('single-tenant/users')}>
                  {t('Användare')}
                </MenuItem>
                <MenuDivider />
              </>
            )}
            <a
              href="https://intercom.help/klangai"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('help_center_clicked')}
              css={css`
                color: var(--text-primary);
                text-decoration: none;
              `}
            >
              <MenuItem>{t('Hjälpcenter')}</MenuItem>
            </a>
            <a
              href="https://status.klang.ai/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('status_page_clicked')}
              css={css`
                color: var(--text-primary);
                text-decoration: none;
              `}
            >
              <MenuItem>{t('Driftstatus')}</MenuItem>
            </a>
            <a
              href="https://app.klang.ai/info/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('terms_of_service_clicked')}
              css={css`
                color: var(--text-primary);
                text-decoration: none;
              `}
            >
              <MenuItem>{t('Användarvillkor')}</MenuItem>
            </a>
            <a
              href="https://app.klang.ai/info/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('privacy_policy_clicked')}
              css={css`
                color: var(--text-primary);
                text-decoration: none;
              `}
            >
              <MenuItem>{t('Integritetspolicy')}</MenuItem>
            </a>
            <MenuDivider />
            <MenuItem
              css={css`
                margin-bottom: 0.5rem;
              `}
              onClick={onLogout}
            >
              {t('Logga ut')}
            </MenuItem>
          </HoverPopover>
          <SpinnerModal open={customerPortalLoading} />
        </>
      )}
    </PopupState>
  );
}
